class PJHeader extends HTMLElement {
  constructor() {
    super();

    this.headerSection = null;
    this.headerType = null;

    this.MobileSearchLocation = {
      OVER: "over",
      BLOCK: "block",
    };

    this.HeaderClass = {
      REGULAR: "regular-header",
    };

    this.ScrollBehavior = {
      HIDE_ON_SCROLL: "hide-on-scroll",
      STICKY: "always-sticky",
      NONE: "none",
    };
  }

  connectedCallback() {
    this.headerSection = document.querySelector(".scx-header--section");
    this.headerType = this.getAttribute("data-scroll-behavior");
    this.headerSensitivity = this.getAttribute("data-scroll-sensitivity");

    this.searchButtonBehavior();
    this.applyScrollBehavior();
  }

  /**
   * This function gets the current header height and sets is as a property.
   * Useful for hide/show header on scroll behavior.
   */
  setHeaderHeight() {
    requestAnimationFrame(() => {
      if (this.headerSection && this.headerSection.offsetHeight > 0) {
        console.log("Setting header height", this.headerSection.offsetHeight);
        document.documentElement.style.setProperty(
          "--header-height",
          `${this.headerSection.offsetHeight}px`
        );
      } else {
        document.documentElement.style.setProperty("--header-height", `60px`);
      }
    });
  }

  /**
   * Applies the correct logic, depending on the selected scroll behavior.
   */
  applyScrollBehavior() {
    switch (this.headerType) {
      case this.ScrollBehavior.HIDE_ON_SCROLL: {
        this.setHeaderHeight();
        window
          .matchMedia("(max-width: 990px)")
          .addEventListener("change", this.setHeaderHeight.bind(this));

        this.hideHeaderOnScroll();
        break;
      }
      case this.ScrollBehavior.NONE: {
        this.addHeaderClass(this.HeaderClass.REGULAR);
        break;
      }
      default: {
        return null;
      }
    }
  }

  /**
   * Applies a class to the header section.
   */
  addHeaderClass(classToApply) {
    if (!this.headerSection || !classToApply) {
      return;
    }

    this.headerSection.classList.add(classToApply);
  }

  /**
   * Initializes search modal open/close behavior.
   *
   * Binds click event listeners to the search button and close button
   * to toggle the visibility of the search modal. Ensures that elements
   * exist before attaching event listeners to prevent runtime errors.
   */
  searchButtonBehavior() {
    this.searchTemplate = this.getAttribute("data-search-template");
    this.searchTemplateLocationMobile = this.getAttribute(
      "data-search-template-location"
    );

    const scx_header = this.querySelector("pj-header");
    const searchButton = this.querySelector("#search-button");
    const closeButton = this.querySelector("#close-search-modal--wrapper");
    const searchModal = this.querySelector("#search-modal--wrapper");
    const body = document.body;

    if (!searchButton || !closeButton || !searchModal) return;
    let outsideClickHandler;
    const toggleModal = (open) => {
      if (
        this.searchTemplateLocationMobile === this.MobileSearchLocation.BLOCK
      ) {
        if (window.innerWidth < 990) {
          const searchBottomElem = this.querySelector("#header--bottom-search");
          searchBottomElem?.classList.toggle("open");
        } else {
          searchModal?.classList.toggle("open", open);
        }

        return;
      }

      searchModal.classList.toggle("open", open);
      searchButton.style.display = open ? "none" : "block";

      if (open) {
        outsideClickHandler = (event) => {
          if (
            !searchModal.contains(event.target) &&
            event.target !== searchButton
          ) {
            toggleModal(false);
            document.removeEventListener("click", outsideClickHandler);
          }
        };
        setTimeout(() => {
          document.addEventListener("click", outsideClickHandler);
        }, 0);
      } else {
        document.removeEventListener("click", outsideClickHandler);
      }
    };

    searchButton.addEventListener("click", () => toggleModal(true));
    closeButton.addEventListener("click", () => toggleModal(false));
  }

  /**
   * Performs logic to hide the section on scroll.
   * Uses requestAnimationFrame for performance reasons.
   */
  hideHeaderOnScroll() {
    if (!this.headerSection) {
      return;
    }

    let lastScrollY = window.scrollY;
    let scrollOffset = parseInt(this.headerSensitivity) ?? 5;
    let ticking = false;

    const hs = this.headerSection;

    function updateHeader() {
      if (!hs || !hs.classList) {
        return;
      }

      if (window.scrollY > lastScrollY + scrollOffset) {
        hs.classList.add("hide-header");
        document.body.classList.remove("header-state-open");
        document.body.classList.add("header-state-hidden");
      } else if (window.scrollY < lastScrollY - scrollOffset) {
        hs.classList.remove("hide-header");
        document.body.classList.remove("header-state-hidden");
        document.body.classList.add("header-state-open");
      }

      lastScrollY = window.scrollY;
      ticking = false;
    }

    window.addEventListener("scroll", function () {
      if (!ticking) {
        requestAnimationFrame(updateHeader);
        ticking = true;
      }
    });
  }
}

customElements.define("pj-header", PJHeader);
